import '../../index.css';

export default function ParentGuide() {
  return (
    <div style={{ minHeight:'100vh', background:'var(--bg-primary)', color:'var(--text-primary)', fontFamily:'var(--font-body)' }}>

      {/* Header */}
      <div style={{ background:'var(--bg-secondary)', borderBottom:'1px solid var(--border)', padding:'24px 40px', display:'flex', alignItems:'center', gap:'16px' }}>
        <img src="/wf-logo.png" alt="Wow Factor Baseball" style={{ width:'40px', height:'40px', objectFit:'contain' }} />
        <div>
          <div style={{ fontFamily:'var(--font-display)', fontSize:'1.1rem', letterSpacing:'0.12em', color:'var(--text-primary)' }}>WOW FACTOR</div>
          <div style={{ fontSize:'0.7rem', color:'var(--text-muted)', letterSpacing:'0.15em', textTransform:'uppercase' }}>Parent & Client Guide</div>
        </div>
      </div>

      <div style={{ maxWidth:'760px', margin:'0 auto', padding:'48px 32px' }}>

        {/* Intro */}
        <div style={{ marginBottom:'48px' }}>
          <h1 style={{ fontFamily:'var(--font-display)', fontSize:'2rem', letterSpacing:'0.06em', marginBottom:'12px' }}>GETTING STARTED</h1>
          <p style={{ color:'var(--text-secondary)', fontSize:'0.95rem', lineHeight:'1.7' }}>
            Welcome to <strong style={{ color:'var(--text-primary)' }}>Wow Factor Baseball</strong>! This guide walks you through everything you need — from creating your account to booking your first training session.
          </p>
        </div>

        <Section title="Creating Your Account">
          <p>Click <Strong>Create one</Strong> on the login page to register. You'll need:</p>
          <Ul items={[
            'Your first and last name',
            'A valid email address',
            'A phone number (optional — used for SMS reminders)',
            'A password (minimum 8 characters)',
          ]} />
          <InfoBox>After registering you'll receive a verification email. Click the link in that email before logging in. Check your spam folder if you don't see it within a few minutes.</InfoBox>
        </Section>

        <Section title="How to Book a Session">
          <p>From your dashboard, click <Strong>Book a Session</Strong> in the sidebar. Booking takes four simple steps:</p>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px', margin:'16px 0' }}>
            {[
              ['1', 'Choose an Instructor', 'Browse available instructors, view their bio and specialties, then click Book with [Name].'],
              ['2', 'Pick Duration, Date & Time', 'Select 30-min or 60-min (if offered), choose a date, then pick an open time slot.'],
              ['3', 'Confirm Your Booking', 'Review the details, optionally select a player and add notes, then click Confirm Booking.'],
              ['4', 'Pay via CashApp', 'After booking a CashApp QR code appears on screen. Payment is due before or at the session.'],
            ].map(([num, title, desc]) => (
              <div key={num} style={{ background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:'var(--radius-md)', padding:'16px' }}>
                <div style={{ width:'24px', height:'24px', background:'var(--text-primary)', color:'var(--bg-primary)', borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'0.75rem', fontWeight:'700', marginBottom:'10px' }}>{num}</div>
                <div style={{ fontWeight:'700', fontSize:'0.875rem', marginBottom:'4px' }}>{title}</div>
                <div style={{ fontSize:'0.825rem', color:'var(--text-secondary)', lineHeight:'1.5' }}>{desc}</div>
              </div>
            ))}
          </div>
          <TipBox>Time slots are held the moment someone books — even before the instructor confirms. If a slot disappears, it's already reserved.</TipBox>
        </Section>

        <Section title="Understanding Session Statuses">
          <p>Track all your sessions under <Strong>My Sessions</Strong> in the sidebar.</p>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(2, 1fr)', gap:'10px', margin:'16px 0' }}>
            {[
              ['Pending',   '#fef9c3', '#854d0e', 'Booked — waiting for instructor to confirm'],
              ['Confirmed', '#dcfce7', '#14532d', 'Instructor confirmed — you\'re all set'],
              ['Completed', '#dbeafe', '#1e3a5f', 'Session took place successfully'],
              ['Cancelled', '#fee2e2', '#7f1d1d', 'Cancelled by you or the instructor'],
              ['No Show',   '#f3f4f6', '#374151', 'Session was missed without prior cancellation'],
            ].map(([label, bg, color, desc]) => (
              <div key={label} style={{ background:bg, borderRadius:'var(--radius-md)', padding:'12px 14px' }}>
                <div style={{ fontSize:'0.75rem', fontWeight:'700', letterSpacing:'0.08em', textTransform:'uppercase', color, marginBottom:'4px' }}>{label}</div>
                <div style={{ fontSize:'0.8rem', color:'#555' }}>{desc}</div>
              </div>
            ))}
          </div>
          <InfoBox>You'll automatically receive an email (and SMS if your phone is on file) 24 hours before any confirmed session.</InfoBox>
        </Section>

        <Section title="Managing Your Players">
          <p>Under <Strong>My Players</Strong> in the sidebar you can add and manage the athletes you're booking for.</p>
          <SubSection title="Adding a Player">
            <Ul items={[
              'Click + Add Player',
              'Enter the player\'s first and last name (required)',
              'Optionally add date of birth, positions, and notes',
              'Click Add Player to save',
            ]} />
          </SubSection>
          <SubSection title="Editing a Player">
            <p>Click the <Strong>Edit</Strong> button on any player card to update their name, date of birth, positions, or notes. You can select multiple positions (e.g. Pitcher + Shortstop) by tapping each one.</p>
          </SubSection>
          <SubSection title="Player Photos">
            <p>Click the <Strong>+</Strong> on a player's avatar to upload a photo. Supported formats: JPEG, PNG, WebP.</p>
          </SubSection>
          <TipBox>iPhone users: HEIC photos are also supported — you can upload directly from your camera roll.</TipBox>
        </Section>

        <Section title="Cancelling a Session">
          <p>Go to <Strong>My Sessions</Strong>, find the session, and click <Strong>Cancel Session</Strong>. A confirmation window will appear asking you to confirm and optionally provide a reason.</p>
          <WarnBox>Sessions can only be cancelled while they are Pending or Confirmed. When you cancel, the instructor is notified automatically.</WarnBox>
        </Section>

        <Section title="Managing Your Profile">
          <p>Go to <Strong>My Profile</Strong> in the sidebar to update your contact info and notification preferences.</p>
          <SubSection title="Notification Preferences">
            <Ul items={[
              'Email notifications — session confirmations, cancellations, and reminders via email',
              'SMS notifications — same updates as text messages (requires a phone number on file)',
            ]} />
          </SubSection>
          <SubSection title="Changing Your Password">
            <p>On the My Profile page click <Strong>Change Password</Strong>, enter your current password, then set a new one.</p>
          </SubSection>
        </Section>

        <Section title="Getting Help & Support">
          <p>If you run into any issue — a booking problem, a billing question, or anything else — you can contact the support team directly from inside the portal. No emails needed.</p>
          <SubSection title="Opening a Support Ticket">
            <Ul items={[
              'Click Support in the sidebar',
              'Click + New Ticket',
              'Enter a short Subject describing your issue',
              'Write your full message in the Message field',
              'Click Submit Ticket — the support team is notified immediately',
            ]} />
          </SubSection>
          <SubSection title="Getting a Reply">
            <p>When the support team responds you'll see two notifications automatically:</p>
            <Ul items={[
              'A blue notification banner at the top of your Dashboard home page',
              'A red badge count on the Support item in the sidebar',
            ]} />
            <p style={{ marginTop:'8px' }}>Both disappear once you open the Support tab and read the reply.</p>
          </SubSection>
          <SubSection title="Continuing the Conversation">
            <p>Click any ticket in your Support list to open the full thread. You'll see your messages and the team's replies in a chat-style view. Type in the reply box at the bottom and click <Strong>Send Reply</Strong> (or press Ctrl+Enter / ⌘+Enter) to respond.</p>
          </SubSection>
          <SubSection title="Ticket Statuses">
            <div style={{ display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap:'10px', margin:'12px 0' }}>
              {[
                ['Open', 'rgba(251,191,36,0.15)', '#b45309', 'Submitted — waiting on the support team'],
                ['In Progress', 'rgba(96,165,250,0.15)', '#1d4ed8', 'Team is actively working on it'],
                ['Resolved', 'rgba(74,222,128,0.15)', '#15803d', 'Issue has been closed'],
              ].map(([label, bg, color, desc]) => (
                <div key={label} style={{ background:bg, borderRadius:'var(--radius-md)', padding:'12px 14px' }}>
                  <div style={{ fontSize:'0.75rem', fontWeight:'700', letterSpacing:'0.08em', textTransform:'uppercase', color, marginBottom:'4px' }}>{label}</div>
                  <div style={{ fontSize:'0.8rem', color:'#555' }}>{desc}</div>
                </div>
              ))}
            </div>
          </SubSection>
          <InfoBox>Once a ticket is marked Resolved you can no longer reply to it. If the issue comes back, simply open a new ticket.</InfoBox>
          <TipBox>All your past tickets stay on file under the Support tab so you can reference previous conversations at any time.</TipBox>
        </Section>

        <Section title="Frequently Asked Questions">
          {[
            ['I booked a session — when will I hear back?', 'Your session shows as Pending until the instructor confirms it. You\'ll receive an email and/or SMS as soon as they do. Most instructors confirm within 24 hours.'],
            ['The time slot I wanted isn\'t available. What happened?', 'Slots are reserved the moment another parent books them — even before the instructor confirms. If a slot disappears, it\'s already taken. Try a different time or date.'],
            ['How do I pay for a session?', 'Payment is via CashApp. After booking, a QR code and payment details appear on screen. Payment is expected before or at the time of the session.'],
            ['I didn\'t receive a verification email.', 'Check your spam folder first. If it\'s not there, log in and use the Resend verification email option on the pending verification page.'],
            ['Can I book for multiple players?', 'Yes. Add each player under My Players, then select the appropriate player when booking each session.'],
            ['The instructor rescheduled my session. What do I do?', 'You\'ll receive a notification showing the original time and the new time. No action is required from you — just show up at the new time.'],
            ['I forgot my password.', 'On the login page click Forgot your password? and enter your email. You\'ll receive a reset link valid for 1 hour.'],
            ['How do I contact support?', 'Click Support in the sidebar, then click + New Ticket. Describe your issue and submit. You\'ll see a badge on the Support tab and a banner on your Dashboard when the team replies — no email required.'],
            ['I submitted a support ticket but the reply option is gone.', 'The ticket was likely marked Resolved. If the issue isn\'t fixed, open a new ticket describing the situation — you can reference your old ticket number.'],
          ].map(([q, a]) => (
            <div key={q} style={{ borderBottom:'1px solid var(--border)', paddingBottom:'16px', marginBottom:'16px' }}>
              <div style={{ fontWeight:'700', fontSize:'0.9rem', marginBottom:'6px' }}>{q}</div>
              <div style={{ color:'var(--text-secondary)', fontSize:'0.875rem', lineHeight:'1.6' }}>{a}</div>
            </div>
          ))}
        </Section>

      </div>

      <div style={{ background:'var(--bg-secondary)', borderTop:'1px solid var(--border)', padding:'24px 40px', textAlign:'center', fontSize:'0.8rem', color:'var(--text-muted)' }}>
        <strong style={{ color:'var(--text-primary)' }}>Wow Factor Baseball</strong> &nbsp;·&nbsp; wowfactortraining.com &nbsp;·&nbsp; Questions? Email us at <a href="mailto:info@wowfactortraining.com" style={{ color:'var(--text-secondary)' }}>info@wowfactortraining.com</a>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom:'40px' }}>
      <div style={{ fontSize:'0.7rem', fontWeight:'700', letterSpacing:'0.18em', textTransform:'uppercase', color:'var(--text-muted)', paddingBottom:'10px', borderBottom:'1px solid var(--border)', marginBottom:'18px' }}>{title}</div>
      {children}
    </div>
  );
}
function SubSection({ title, children }) {
  return (
    <div style={{ marginTop:'14px' }}>
      <div style={{ fontWeight:'700', fontSize:'0.875rem', marginBottom:'6px', color:'var(--text-primary)' }}>{title}</div>
      {children}
    </div>
  );
}
function Strong({ children }) {
  return <strong style={{ color:'var(--text-primary)' }}>{children}</strong>;
}
function Ul({ items }) {
  return (
    <ul style={{ paddingLeft:'20px', margin:'8px 0' }}>
      {items.map(i => <li key={i} style={{ color:'var(--text-secondary)', fontSize:'0.875rem', marginBottom:'5px', lineHeight:'1.5' }}>{i}</li>)}
    </ul>
  );
}
function InfoBox({ children }) {
  return <div style={{ background:'rgba(59,91,219,0.1)', borderLeft:'3px solid #3b5bdb', borderRadius:'4px', padding:'12px 16px', margin:'14px 0', fontSize:'0.85rem', color:'var(--text-secondary)', lineHeight:'1.6' }}>{children}</div>;
}
function TipBox({ children }) {
  return <div style={{ background:'rgba(34,197,94,0.1)', borderLeft:'3px solid #22c55e', borderRadius:'4px', padding:'12px 16px', margin:'14px 0', fontSize:'0.85rem', color:'var(--text-secondary)', lineHeight:'1.6' }}>{children}</div>;
}
function WarnBox({ children }) {
  return <div style={{ background:'rgba(249,115,22,0.1)', borderLeft:'3px solid #f97316', borderRadius:'4px', padding:'12px 16px', margin:'14px 0', fontSize:'0.85rem', color:'var(--text-secondary)', lineHeight:'1.6' }}>{children}</div>;
}
